import {
  applyAction,
  cardCode,
  currentChainScore,
  legalActions,
  type Action,
  type MatchState,
  type PlayLinkAction,
  type Side,
} from '../engine';

/** Link actions with duplicate cards and duplicate Wild collaterals merged (same resulting move). */
function distinctLinks(s: MatchState, side: Side): PlayLinkAction[] {
  const seen = new Map<string, PlayLinkAction>();
  for (const a of legalActions(s, side)) {
    if (a.type !== 'PLAY_LINK') continue;
    const card = s.cards[a.cardId];
    if (!card) continue;
    const target =
      'to' in a.target
        ? `${a.target.to.x}${a.target.to.y}`
        : 'hops' in a.target
          ? a.target.hops.map((h) => `${h.x}${h.y}`).join('.')
          : JSON.stringify(a.target.trick);
    const face = a.wild ? `W${a.wild.face.color}${a.wild.face.number}` : cardCode(card);
    const key = `${face}|${a.pawnId ?? ''}|${target}`;
    if (!seen.has(key)) seen.set(key, a);
  }
  return [...seen.values()];
}

export interface BestChain {
  /** Highest chain score reachable this action (P × M), or 0 when no chain is possible. */
  readonly score: number;
  readonly actions: readonly Action[];
  /** False when the node budget ran out before the search finished. */
  readonly complete: boolean;
}

/**
 * The best immediate chain score available to `side` from an ACTION or CHAIN state
 * (telemetry §21.4). A bounded depth-first search over legal links.
 */
export function bestChainScore(state: MatchState, side: Side, nodeBudget = 4000): BestChain {
  let nodes = 0;
  let best: BestChain = { score: 0, actions: [], complete: true };
  const baseScore = state.players[side].roundScore;
  const visit = (s: MatchState, path: Action[]): void => {
    for (const a of distinctLinks(s, side)) {
      if (nodes >= nodeBudget) return;
      nodes++;
      const r = applyAction(s, a);
      if (r.error) continue;
      const next = [...path, a];
      const open = r.state.phase === 'CHAIN' && r.state.decider === side;
      const score = open
        ? (currentChainScore(r.state)?.score ?? 0)
        : (() => {
            const scored = r.events.find((e) => e.type === 'ChainScored' && e.side === side);
            return scored && scored.type === 'ChainScored'
              ? scored.breakdown.score
              : r.state.players[side].roundScore - baseScore;
          })();
      if (score > best.score) best = { score, actions: next, complete: true };
      if (open) visit(r.state, next);
    }
  };
  if (state.decider === side && (state.phase === 'ACTION' || state.phase === 'CHAIN'))
    visit(state, []);
  return { ...best, complete: nodes < nodeBudget };
}
